<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle :pageTitle="isEditing ? 'Edit Payment Channel' : 'Add Payment Channel'" subTitle="Transactions" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-4">
        <form @submit.prevent="submitForm">
          <div class="row">
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Channel Type</label>
              <select class="form-select" v-model="form.channel_type" required>
                <option value="BANK">Bank Transfer</option>
                <option value="EWALLET">E-Wallet</option>
                <option value="QRIS">QRIS</option>
              </select>
            </div>
            
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Provider Name (e.g. BCA, OVO)</label>
              <input type="text" class="form-control" v-model="form.provider_name" placeholder="BCA" required />
            </div>
            
            <div class="col-lg-6 col-md-6 mb-4" v-if="form.channel_type !== 'QRIS'">
              <label class="form-label text-dark fs-14 fw-medium">Account Number</label>
              <input type="text" class="form-control" v-model="form.account_number" />
            </div>

            <div class="col-lg-6 col-md-6 mb-4" v-if="form.channel_type !== 'QRIS'">
              <label class="form-label text-dark fs-14 fw-medium">Account Name</label>
              <input type="text" class="form-control" v-model="form.account_name" />
            </div>

            <div class="col-lg-12 col-md-12 mb-4" v-if="form.channel_type === 'QRIS'">
              <label class="form-label text-dark fs-14 fw-medium">QR Image</label>
              <div v-if="imagePreview" class="mb-3">
                <img :src="imagePreview" alt="QR Preview" class="img-thumbnail" style="max-height: 200px;" />
              </div>
              <input class="form-control" type="file" id="qrImage" @change="handleImageUpload" accept="image/*">
            </div>
            
            <div class="col-lg-12 col-md-12 mb-4">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="isActive" v-model="form.is_active">
                <label class="form-check-label" for="isActive">Active</label>
              </div>
            </div>
          </div>
          
          <div class="d-flex justify-content-end gap-2">
            <RouterLink to="/payment-channels" class="btn btn-secondary fs-15 fw-medium">Cancel</RouterLink>
            <button type="submit" class="btn btn-primary fs-15 fw-medium" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ isEditing ? 'Update Channel' : 'Create Channel' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PageTitle from '@/components/Common/PageTitle.vue';
import paymentChannelService from '@/services/paymentChannelService';

export default defineComponent({
  name: 'PaymentChannelFormPage',
  components: {
    PageTitle
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    
    const isEditing = ref(false);
    const channelId = ref<number | null>(null);
    const loading = ref(false);
    
    const form = ref({
      channel_type: 'BANK',
      provider_name: '',
      account_number: '',
      account_name: '',
      is_active: true
    });

    const imageFile = ref<File | null>(null);
    const imagePreview = ref<string | null>(null);

    const handleImageUpload = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        imageFile.value = target.files[0];
        imagePreview.value = URL.createObjectURL(target.files[0]);
      }
    };

    onMounted(async () => {
      if (route.params.id) {
        isEditing.value = true;
        channelId.value = Number(route.params.id);
        await fetchChannel(channelId.value);
      }
    });

    const fetchChannel = async (id: number) => {
      try {
        const response = await paymentChannelService.getById(id);
        const data = response.data;
        form.value = {
          channel_type: data.channel_type,
          provider_name: data.provider_name,
          account_number: data.account_number || '',
          account_name: data.account_name || '',
          is_active: data.is_active
        };
        if (data.qr_image_file) {
          const baseUrl = process.env.NODE_ENV === 'production' 
            ? 'https://backend-beta.rayacheck.id' 
            : 'http://jurnal-backend.test';
          imagePreview.value = `${baseUrl}/storage/${data.qr_image_file}`;
        }
      } catch (error) {
        console.error('Error fetching channel:', error);
        router.push('/payment-channels');
      }
    };

    const submitForm = async () => {
      loading.value = true;
      try {
        let payload: any = form.value;

        // Use FormData if we are uploading a QR image
        if (form.value.channel_type === 'QRIS' && imageFile.value) {
          const formData = new FormData();
          formData.append('channel_type', form.value.channel_type);
          formData.append('provider_name', form.value.provider_name);
          formData.append('account_name', form.value.account_name);
          formData.append('is_active', form.value.is_active ? '1' : '0');
          formData.append('qr_image_file', imageFile.value);
          if (form.value.account_number) {
            formData.append('account_number', form.value.account_number);
          }
          payload = formData;
        }

        if (isEditing.value && channelId.value) {
          await paymentChannelService.update(channelId.value, payload);
        } else {
          await paymentChannelService.create(payload);
        }
        router.push('/payment-channels');
      } catch (error) {
        console.error('Error saving channel:', error);
      } finally {
        loading.value = false;
      }
    };

    return {
      isEditing,
      form,
      loading,
      imagePreview,
      handleImageUpload,
      submitForm
    };
  }
});
</script>
